import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { adminCalendar, adminToggleBlockedDate } from "../../lib/api";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function buildBookingMap(bookings) {
  // date -> [{property_id, full_name, status}]
  const map = new Map();
  for (const b of bookings || []) {
    if (!b.check_in || !b.check_out) continue;
    const start = new Date(b.check_in);
    const end = new Date(b.check_out);
    for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
      const key = `${b.property_id}|${ymd(d)}`;
      const list = map.get(key) || [];
      list.push({ id: b.id, full_name: b.full_name, status: b.status });
      map.set(key, list);
    }
  }
  return map;
}

export default function CalendarPanel() {
  const today = useMemo(() => { const t = new Date(); t.setHours(0,0,0,0); return t; }, []);
  const [view, setView] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ properties: [], bookings: [] });
  const [busy, setBusy] = useState(new Set()); // pending cell keys
  const [dragging, setDragging] = useState(null); // { pid, mode: "block"|"unblock" }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminCalendar();
      setData(d);
    } catch {
      toast.error("Could not load calendar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const daysInView = useMemo(() => {
    const last = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    const arr = [];
    for (let i = 1; i <= last; i++) arr.push(new Date(view.getFullYear(), view.getMonth(), i));
    return arr;
  }, [view]);

  const bookingMap = useMemo(() => buildBookingMap(data.bookings), [data.bookings]);

  const toggle = async (pid, dateStr) => {
    const cellKey = `${pid}|${dateStr}`;
    if (busy.has(cellKey)) return;
    setBusy((s) => new Set(s).add(cellKey));

    // optimistic update
    setData((prev) => ({
      ...prev,
      properties: prev.properties.map((p) => {
        if (p.id !== pid) return p;
        const cur = new Set(p.blocked_dates || []);
        if (cur.has(dateStr)) cur.delete(dateStr); else cur.add(dateStr);
        return { ...p, blocked_dates: Array.from(cur).sort() };
      }),
    }));

    try {
      await adminToggleBlockedDate(pid, dateStr);
    } catch {
      toast.error("Could not update");
      load();
    } finally {
      setBusy((s) => { const n = new Set(s); n.delete(cellKey); return n; });
    }
  };

  const onMouseDownCell = (pid, dateStr, isBlocked, isBooked) => () => {
    if (isBooked) return; // never overwrite booked nights via DnD
    setDragging({ pid, mode: isBlocked ? "unblock" : "block" });
    toggle(pid, dateStr);
  };
  const onMouseEnterCell = (pid, dateStr, isBlocked, isBooked) => () => {
    if (!dragging || dragging.pid !== pid || isBooked) return;
    if (dragging.mode === "block" && !isBlocked) toggle(pid, dateStr);
    if (dragging.mode === "unblock" && isBlocked) toggle(pid, dateStr);
  };
  useEffect(() => {
    const up = () => setDragging(null);
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);

  const prevMonth = () => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1));
  const nextMonth = () => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1));
  const goToday  = () => setView(new Date(today.getFullYear(), today.getMonth(), 1));

  return (
    <div data-testid="calendar-panel" className="select-none">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="ed-overline mb-2">Operations</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Calendar</h1>
          <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5">Multi-property month view. Click or drag to block / unblock dates. Booked nights are locked.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={prevMonth} className="w-9 h-9 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] flex items-center justify-center text-[var(--ed-ink-soft)] rounded-sm">
            <ChevronLeft size={15} />
          </button>
          <p data-testid="cal-month" className="font-serif text-lg text-[var(--ed-ink)] min-w-[180px] text-center">
            {MONTHS[view.getMonth()]} {view.getFullYear()}
          </p>
          <button data-testid="cal-next" onClick={nextMonth} className="w-9 h-9 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] flex items-center justify-center text-[var(--ed-ink-soft)] rounded-sm">
            <ChevronRight size={15} />
          </button>
          <button data-testid="cal-today" onClick={goToday} className="text-[0.6rem] uppercase tracking-[0.22em] border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] px-3 py-2 rounded-sm">
            Today
          </button>
          <button data-testid="cal-refresh" onClick={load} className="text-[0.6rem] uppercase tracking-[0.22em] border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] px-3 py-2 rounded-sm flex items-center gap-1.5">
            <RefreshCw size={11} />
          </button>
        </div>
      </header>

      {/* Legend */}
      <div className="flex flex-wrap gap-5 text-xs text-[var(--ed-ink-soft)] mb-5">
        <span className="flex items-center gap-2"><i className="w-3 h-3 bg-[var(--ed-paper)] border border-[var(--ed-rule)] inline-block" /> Free</span>
        <span className="flex items-center gap-2"><i className="w-3 h-3 bg-[var(--ed-ink)] inline-block" /> Blocked</span>
        <span className="flex items-center gap-2"><i className="w-3 h-3 bg-[var(--ed-emerald)] inline-block" /> Paid booking</span>
        <span className="flex items-center gap-2"><i className="w-3 h-3 bg-amber-400 inline-block" /> Pending payment</span>
      </div>

      {loading ? (
        <p className="text-[var(--ed-ink-muted)] py-16 text-center">Loading calendar…</p>
      ) : data.properties.length === 0 ? (
        <div className="border border-dashed border-[var(--ed-rule)] p-12 text-center rounded-sm">
          <p className="text-[var(--ed-ink-muted)]">No residences yet — add one in Properties.</p>
        </div>
      ) : (
        <div className="border border-[var(--ed-rule)] rounded-sm overflow-x-auto bg-[var(--ed-bg)]">
          <table className="text-xs" style={{ minWidth: `${220 + daysInView.length * 32}px` }}>
            <thead>
              <tr className="bg-[var(--ed-paper)]/60">
                <th className="text-left px-4 py-3 text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ed-ink-muted)] sticky left-0 bg-[var(--ed-paper)] z-10 border-r border-[var(--ed-rule)] min-w-[200px]">Residence</th>
                {daysInView.map((d) => {
                  const isToday = ymd(d) === ymd(today);
                  const dow = (d.getDay() + 6) % 7; // 0=Mon
                  return (
                    <th key={ymd(d)} className={`px-0 py-1.5 font-normal min-w-[32px] ${dow >= 5 ? "bg-[var(--ed-paper)]/40" : ""}`}>
                      <div className={`text-[0.55rem] uppercase tracking-[0.18em] ${isToday ? "text-[var(--ed-emerald)] font-semibold" : "text-[var(--ed-ink-muted)]"}`}>
                        {["M","T","W","T","F","S","S"][dow]}
                      </div>
                      <div className={`text-xs ${isToday ? "text-[var(--ed-emerald)] font-semibold" : "text-[var(--ed-ink)]"}`}>{d.getDate()}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.properties.map((p) => {
                const blocked = new Set(p.blocked_dates || []);
                return (
                  <tr key={p.id} className="border-t border-[var(--ed-rule)]">
                    <td className="px-4 py-2 sticky left-0 bg-[var(--ed-bg)] z-10 border-r border-[var(--ed-rule)] min-w-[200px]">
                      <p className="text-[var(--ed-ink)] truncate font-medium">{p.name}</p>
                      <p className="text-[var(--ed-ink-muted)] text-[0.65rem] truncate">{p.location}</p>
                      {p.published === false && <span className="text-[0.55rem] uppercase tracking-widest text-[var(--ed-ink-muted)]">Hidden</span>}
                    </td>
                    {daysInView.map((d) => {
                      const ds = ymd(d);
                      const isBlocked = blocked.has(ds);
                      const bookings = bookingMap.get(`${p.id}|${ds}`) || [];
                      const hasPaid = bookings.some((x) => x.status === "paid");
                      const hasPending = bookings.some((x) => x.status === "pending_payment");
                      const isBooked = hasPaid || hasPending;
                      const tip = bookings.length ? bookings.map((b) => `${b.full_name} · ${b.status}`).join("\n") : "";
                      let cls = "bg-[var(--ed-paper)] hover:bg-[var(--ed-paper)]/80";
                      if (isBlocked) cls = "bg-[var(--ed-ink)]";
                      if (hasPending) cls = "bg-amber-400";
                      if (hasPaid) cls = "bg-[var(--ed-emerald)]";
                      const cellKey = `${p.id}|${ds}`;
                      const isBusyCell = busy.has(cellKey);
                      return (
                        <td
                          key={ds}
                          data-testid={`cal-cell-${p.id}-${ds}`}
                          title={tip || (isBlocked ? "Blocked" : "Free")}
                          onMouseDown={onMouseDownCell(p.id, ds, isBlocked, isBooked)}
                          onMouseEnter={onMouseEnterCell(p.id, ds, isBlocked, isBooked)}
                          className={`p-0 w-[32px] h-[32px] cursor-${isBooked ? "not-allowed" : "pointer"} border-r border-b border-[var(--ed-rule)] transition-colors ${cls} ${isBusyCell ? "opacity-60" : ""}`}
                        />
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
